import React, {useEffect, useState} from 'react';

import classNames from 'classnames';
import {connect} from 'react-redux';

import {Feature, useFeature, useFeatureWithAnalytics} from 'components/feature';
import {ApplicationStoreType} from 'desktop/store';
import {OrderReviewTotalsItemsType} from 'desktop/types/orderReviewTotalsType';
import {StatusType} from 'desktop/types/statusType';

import {isUhcEnabled} from 'libs/rollout';

import {
    getOrderReviewTotalItems,
    getOrderReviewTotalItemsCount,
    getTotalRebateSum,
} from '../../store/selectors/orderReviewTotals/orderReviewTotals';
import {CartGrandTotalConnected} from '../cartGrandTotal/cartGrandTotal';
import {CartHeader} from '../cartHeader/cartHeader';
import {CartItemConnected} from '../cartItem/cartItem';
import {CartItemFacelift} from '../cartItemFacelift/cartItemFacelift';
import {CartItemPlaceholder} from '../cartItemPlaceholder/cartItemPlaceholder';
import {CartTotalsConnected} from '../cartTotals/cartTotals';
import {CartUhcLogo} from '../cartUhcLogo/cartUhcLogo';

import {useScrollStates} from './useScrollStates';

import s from './cart.scss';

export type PropsType = {
    items: OrderReviewTotalsItemsType[];
    qty: number;
    totalRebate: number;
    status: StatusType;
};

/**
 * Cart for checkout page
 *
 * @constructor
 */
export const Cart: React.FC<PropsType> = ({items, qty, status, totalRebate}) => {
    const isFacelift = useFeature('dCheckoutFacelift');

    const [itemsDetailsStates, setItemsDetailsStates] = useState<
        Array<{id: number; isDetailsOpen: boolean}>
    >([]);
    const isClRebate = useFeatureWithAnalytics('clRebate', false, 'desktop') && totalRebate;

    useEffect(() => {
        if (items.length > 0) {
            setItemsDetailsStates(items.map(({id}) => ({id, isDetailsOpen: false})));
        }
    }, [items]);

    const {handleScroll, isScrollOnBottom, isScrollOnTop, scrollRef} =
        useScrollStates(itemsDetailsStates);

    const onDetailsClick = (currentId: number) => {
        setItemsDetailsStates(prev =>
            prev.map(item => {
                if (item?.id === currentId) {
                    return {...item, isDetailsOpen: !item.isDetailsOpen};
                }
                return item;
            }),
        );
    };

    return (
        <div
            className={classNames(s.cartWrapperContainer, {
                [s.cartWrapperContainerFacelift]: isFacelift && itemsDetailsStates.length > 1,
                [s.cartWrapperContainerFaceliftSm]: isFacelift && itemsDetailsStates.length <= 1,
                [s.cartWrapperContainerSm]: itemsDetailsStates.length <= 1,
            })}
        >
            {isUhcEnabled() && <CartUhcLogo />}
            <CartHeader count={qty} isScrollOnTop={isScrollOnTop} />
            <div
                aria-label="scroll container"
                className={classNames(s.wrapper, {[s.isClRebate]: isClRebate})}
                onScroll={handleScroll}
                ref={scrollRef}
            >
                <Feature
                    fallback={
                        status.deliveryStep.loaded && itemsDetailsStates.length !== 0 ? (
                            itemsDetailsStates.map(item => (
                                <CartItemConnected
                                    isOpen={item.isDetailsOpen}
                                    itemId={item.id}
                                    key={item.id}
                                    onDetailsClick={onDetailsClick}
                                />
                            ))
                        ) : (
                            <CartItemPlaceholder />
                        )
                    }
                    flag="dCheckoutFacelift"
                    platform="desktop"
                >
                    <CartItemFacelift />
                </Feature>
                <CartTotalsConnected />
            </div>
            <CartGrandTotalConnected isScrollOnBottom={isScrollOnBottom} />
        </div>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    items: getOrderReviewTotalItems(store),
    qty: getOrderReviewTotalItemsCount(store),
    status: store.checkoutSideReview.status,
    totalRebate: getTotalRebateSum(store),
});

export const CartConnected = connect(mapStateToProps)(Cart);
